import { useState, useEffect, useRef } from 'react';
import kuromoji from 'kuromoji';
import useKuromoji from './useKuromoji';
import WordPopup, { WordResult } from './WordPopup';
import TranslatePopup from './TranslatePopup';
import { fetchWord, wordFromToken, prefetchDefinitions } from './japaneseUtils';

interface ClickedWord {
  word: string;
  reading: string;
  rect: DOMRect;
  jishoResult: WordResult | null;
  fetching: boolean;
}

interface SelectionData {
  text: string;
}

interface Props {
  text: string;
}

export default function ClickableJapaneseText({ text }: Props) {
  const { tokenize, getReading, loading } = useKuromoji();
  const [tokens, setTokens] = useState<kuromoji.IpadicFeatures[]>([]);
  const [clickedWord, setClickedWord] = useState<ClickedWord | null>(null);
  const [pendingSelection, setPendingSelection] = useState<SelectionData | null>(null);
  const [selectionData, setSelectionData] = useState<SelectionData | null>(null);
  const [cache] = useState(() => new Map<string, WordResult | null>());
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (loading) return;
    const newTokens = tokenize(text);
    setTokens(newTokens);
    prefetchDefinitions(newTokens, cache);
  }, [text, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    const handleSelectionChange = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const selection = window.getSelection();
        const selectedText = selection?.toString().trim();

        if (!selectedText || !containerRef.current) {
          setPendingSelection(null);
          return;
        }
        const anchorNode = selection?.anchorNode;
        if (!anchorNode || !containerRef.current.contains(anchorNode)) {
          setPendingSelection(null);
          return;
        }
        setPendingSelection({ text: selectedText });
      }, 150);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      clearTimeout(timer);
    };
  }, []);

  const handleTranslateButtonClick = () => {
    if (!pendingSelection) return;
    setSelectionData(pendingSelection);
    setPendingSelection(null);
    setClickedWord(null);
    window.getSelection()?.removeAllRanges();
  };

  const handleWordClick = async (token: kuromoji.IpadicFeatures, e: React.MouseEvent<HTMLSpanElement>) => {
    if (window.getSelection()?.toString().trim()) return;

    const word = wordFromToken(token);
    const reading = getReading(token);
    const rect = e.currentTarget.getBoundingClientRect();

    setSelectionData(null);
    setPendingSelection(null);

    if (cache.has(word)) {
      setClickedWord({ word, reading, rect, jishoResult: cache.get(word) ?? null, fetching: false });
      return;
    }

    setClickedWord({ word, reading, rect, jishoResult: null, fetching: true });
    let result: WordResult | null = null;
    try { result = await fetchWord(word); } catch {}
    cache.set(word, result);
    setClickedWord(prev => prev?.word === word ? { ...prev, jishoResult: result, fetching: false } : prev);
  };

  return (
    <>
      <span ref={containerRef}>
        {tokens.map((token, i) => (
          <span
            key={i}
            className="cursor-pointer hover:text-white transition-colors duration-100"
            onClick={(e) => handleWordClick(token, e)}
          >
            {token.surface_form}
          </span>
        ))}
      </span>

      {pendingSelection && (
        <button
          className="fixed z-20 bottom-6 right-6 px-4 py-2 bg-zinc-800 border border-zinc-600 text-zinc-200 text-xs rounded-full shadow-xl hover:bg-zinc-700 transition-colors duration-100"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleTranslateButtonClick}
        >
          translate
        </button>
      )}

      {clickedWord && (
        <WordPopup
          word={clickedWord.word}
          reading={clickedWord.reading}
          rect={clickedWord.rect}
          result={clickedWord.jishoResult}
          fetching={clickedWord.fetching}
          onClose={() => setClickedWord(null)}
        />
      )}
      {selectionData && (
        <TranslatePopup
          text={selectionData.text}
          onClose={() => setSelectionData(null)}
        />
      )}
    </>
  );
}
